/**
 * Site-wide footer
 */
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { AppDownloadIcons } from 'components/AppDownloads';
import { LocaleSwitcherContainer } from 'components/LocaleSwitcher/LocaleSwitcherContainer';
import cx from 'classnames';
import css from './Footer.scss';

export const Footer = ({ showFooter = true }) =>
  showFooter && (
    <footer className={css.base}>
      <div className="wrap">
        <div className={css.primaryLinks}>
          <div className="grid">
            <div className="grid__item 1/2 md-1/4">
              <h5 className={css.header}>
                <FormattedMessage id="footer.company" defaultMessage="Company" />
              </h5>
              <ul>
                <li>
                  <Link to="/about" target="_self">
                    <FormattedMessage id="footer.about-link" defaultMessage="About us" />
                  </Link>
                </li>
                <li>
                  <Link to="/fitness">
                    <FormattedMessage id="footer.fitness-link" defaultMessage="Why studio fitness" />
                  </Link>
                </li>
                <li>
                  <Link to="/gifts">
                    <FormattedMessage id="footer.gifts-link" defaultMessage="Gifts" />
                  </Link>
                </li>
                <li>
                  <Link to="/about/careers" target="_self">
                    <FormattedMessage id="footer.jobs-link" defaultMessage="Careers" />
                  </Link>
                </li>
                <li>
                  <a href="/press-room" target="_self">
                    <FormattedMessage id="footer.press-link" defaultMessage="Press" />
                  </a>
                </li>
                <li>
                  <Link to="/go" target="_self">
                    <FormattedMessage id="footer.classpass-go-link" defaultMessage="ClassPass Go" />
                  </Link>
                </li>
                <li>
                  <Link to="/try/corporate" target="_self">
                    <FormattedMessage id="footer.corporate" defaultMessage="Corporate Memberships" />
                  </Link>
                </li>
              </ul>
            </div>
            <div className="grid__item 1/2 md-1/4">
              <h5 className={css.header}>
                <FormattedMessage id="footer.support" defaultMessage="Support" />
              </h5>
              <ul>
                <li>
                  <Link to="/contact" target="_self">
                    <FormattedMessage id="footer.contact-link" defaultMessage="Contact" />
                  </Link>
                </li>
                <li>
                  <a href="//help.classpass.com" target="_blank" rel="noopener noreferrer">
                    <FormattedMessage id="footer.help-center-link" defaultMessage="Help Center" />
                  </a>
                </li>
              </ul>
              <Fragment>
                <h5 className={cx('locale-switcher', css.header)}>
                  <FormattedMessage id="footer.language" defaultMessage="Language" />
                </h5>
                <LocaleSwitcherContainer />
              </Fragment>
            </div>
            <div className="grid__item 1/2 md-1/4">
              <h5 className={css.header}>
                <FormattedMessage id="footer.partners" defaultMessage="Partners" />
              </h5>
              <ul>
                <li>
                  <a href="//studios.classpass.com">
                    <FormattedMessage id="footer.studios-link" defaultMessage="Become a Partner" />
                  </a>
                </li>
                <li>
                  <a href="https://developers.classpass.com/spec">
                    <FormattedMessage id="footer.open-api-spec-link" defaultMessage="Integration API Docs" />
                  </a>
                </li>
                <li>
                  <a href="//afterclass.classpass.com" target="_blank" rel="noopener noreferrer">
                    <FormattedMessage id="footer.studios-blog-link" defaultMessage="After Class Blog" />
                  </a>
                </li>
              </ul>
            </div>
            <div className="grid__item 1/2 md-1/4">
              <h5 className={css.header}>
                <FormattedMessage id="footer.community" defaultMessage="Community" />
              </h5>
              <ul>
                <li>
                  <a href="/blog" target="_self">
                    <FormattedMessage id="footer.blog-link" defaultMessage="Blog" />
                  </a>
                </li>
                <li>
                  <a href="/lists/cities" target="_self">
                    <FormattedMessage id="footer.top-lists-link" defaultMessage="Top 10 Lists" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className={css.secondaryLinks}>
          <div className="grid">
            <div className="grid__item md-1/2">
              <h5 className={cx(css.header, 'mt0')}>
                <FormattedMessage id="footer.downloads" defaultMessage="Enjoy fitness on the go" />
              </h5>
              <AppDownloadIcons />
            </div>
            <div className="grid__item md-1/2">
              <div className={css.icons}>
                <ul>
                  <li>
                    <a
                      className="icon icon--28 icon--fb-white"
                      href="//www.facebook.com/classpass"
                      target="_blank"
                      rel="noopener noreferrer">
                      <FormattedMessage id="service.facebook" defaultMessage="Facebook" />
                    </a>
                  </li>
                  <li>
                    <a
                      className="icon icon--28 icon--tw-white"
                      href="//twitter.com/classpass"
                      target="_blank"
                      rel="noopener noreferrer">
                      <FormattedMessage id="service.twitter" defaultMessage="Twitter" />
                    </a>
                  </li>
                  <li>
                    <a
                      className="icon icon--28 icon--ig-white"
                      href="//instagram.com/classpass"
                      target="_blank"
                      rel="noopener noreferrer">
                      <FormattedMessage id="service.instagram" defaultMessage="Instagram" />
                    </a>
                  </li>
                  <li>
                    <a
                      className="icon icon--28 icon--pt-white"
                      href="//www.pinterest.com/classpass/"
                      target="_blank"
                      rel="noopener noreferrer">
                      <FormattedMessage id="service.pinterest" defaultMessage="Pinterest" />
                    </a>
                  </li>
                  <li>
                    <a
                      className="icon icon--28 icon--sp-white"
                      href="//play.spotify.com/user/classpass/"
                      target="_blank"
                      rel="noopener noreferrer">
                      <FormattedMessage id="service.spotify" defaultMessage="Spotify" />
                    </a>
                  </li>
                  <li>
                    <a
                      className="icon icon--28 icon--tb-white"
                      href="//classpass.tumblr.com"
                      target="_blank"
                      rel="noopener noreferrer">
                      <FormattedMessage id="service.tumblr" defaultMessage="Tumblr" />
                    </a>
                  </li>
                </ul>
                <ul className={css.legal}>
                  <li>
                    <a href="/terms" target="_self">
                      <FormattedMessage id="footer.terms-link" defaultMessage="Terms of Use" />
                    </a>
                  </li>
                  <li>
                    <a href="/privacy" target="_self">
                      <FormattedMessage id="footer.privacy-link" defaultMessage="Privacy Policy" />
                    </a>
                  </li>
                  <li>
                    <a href="/privacy#cookies" target="_self">
                      <FormattedMessage id="footer.cookies-link" defaultMessage="Cookies & ads" />
                    </a>
                  </li>
                  <li>
                    <a href="/about/accessibility" target="_self">
                      <FormattedMessage id="footer.accessibility-link" defaultMessage="Accessibility" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
Footer.propTypes = {
  showFooter: PropTypes.bool,
};

export const mapStateToProps = state => ({
  showFooter: !state.page.reducedFooter,
});

export const FooterContainer = connect(mapStateToProps)(Footer);
